import { useCallback } from "react"
import { useCompanyConnectorControllerGetCompanyConnectorsQuery } from "~/ui-rtk/api/companyConnectorApi"
import { ConnectionStatus } from "../api/types"
import { CONNECTORS_IDS_GROUPED } from "~/ui-rtk/constants/sources"

export default function useConnectedServices() {
  const {
    data: connectedServices,
    refetch: refetchConnectors,
    isError: isGetCompanyConnectorsError,
    isLoading: isGetCompanyConnectorsLoading,
  } = useCompanyConnectorControllerGetCompanyConnectorsQuery()

  // Exact-match lookup. Use this when you need the CompanyConnector for a
  // specific service id — typically for action targeting (re-auth, OAuth init,
  // status dialogs, addConnection). Returns undefined when no CC exists with
  // exactly this serviceName, so callers fall through to fresh-create flows
  // instead of accidentally acting on a sibling-service CC.
  const getCompanyConnectorByService = useCallback(
    (service: string) =>
      connectedServices?.find(s => s.serviceName === service),
    [connectedServices],
  )

  // Group-aware lookup. Use this for visibility / feature-detection checks
  // that should survive the Fivetran → Airbyte migration: "is this service
  // family connected anywhere?" returns whichever group sibling exists. The
  // returned CC may have a different serviceName than `service`, so DO NOT use
  // its id/credentials for actions — use getCompanyConnectorByService for that.
  const findCompanyConnectorInGroup = useCallback(
    (service: string) => {
      const exactMatch = connectedServices?.find(s => s.serviceName === service)
      if (exactMatch) {
        return exactMatch
      }

      const groupedEntry = Object.entries(CONNECTORS_IDS_GROUPED).find(
        ([_, connectorIds]) => connectorIds.includes(service),
      )

      if (groupedEntry) {
        const [, connectorIds] = groupedEntry
        return connectedServices?.find(s =>
          connectorIds.includes(s.serviceName),
        )
      }

      return undefined
    },
    [connectedServices],
  )

  return {
    connectedServices,
    refetchConnectors,
    isGetCompanyConnectorsError,
    isGetCompanyConnectorsLoading,
    getCompanyConnectorByService,
    findCompanyConnectorInGroup,
  }
}

export function useCheckServiceIsConnected(service: string) {
  const { connectedServices } = useConnectedServices()

  // Find the key in CONNECTORS_IDS_GROUPED that contains this service
  const groupedEntry = Object.entries(CONNECTORS_IDS_GROUPED).find(
    ([_, connectorIds]) => connectorIds.includes(service),
  )

  // If found in grouped, check if ANY connector in that group is connected
  if (groupedEntry) {
    const [, connectorIds] = groupedEntry
    return (
      connectedServices?.some(
        s =>
          connectorIds.includes(s.serviceName) &&
          s.status === ConnectionStatus.CONNECTED,
      ) ?? false
    )
  }

  // Fallback to original behavior for services not in grouped object
  const connector = connectedServices?.find(s => s.serviceName === service)
  return connector?.status === ConnectionStatus.CONNECTED || false
}

export function useCheckServiceWasConnected(service: string) {
  const { connectedServices } = useConnectedServices()

  // Find the key in CONNECTORS_IDS_GROUPED that contains this service
  const groupedEntry = Object.entries(CONNECTORS_IDS_GROUPED).find(
    ([_, connectorIds]) => connectorIds.includes(service),
  )

  const validStatuses = [
    ConnectionStatus.CONNECTED,
    ConnectionStatus.PENDING_RECONNECT,
    ConnectionStatus.PENDING_SCHEMA_UPDATE,
  ]

  // If found in grouped, check if ANY connector in that group was connected
  if (groupedEntry) {
    const [, connectorIds] = groupedEntry
    return (
      connectedServices?.some(
        s =>
          connectorIds.includes(s.serviceName) &&
          validStatuses.includes(s.status),
      ) ?? false
    )
  }

  // Fallback to original behavior for services not in grouped object
  const connector = connectedServices?.find(s => s.serviceName === service)
  return connector ? validStatuses.includes(connector.status) : false
}
